import { useState, useEffect, useRef } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { db } from '../services/firebase'
import { doc, onSnapshot } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../services/firebase'
import { approveIdentification, rejectIdentification } from '../services/identificationService'
import './ResultsPage.css'

function ResultsPage() {
    const { id } = useParams()
    const navigate = useNavigate()
    const [result, setResult] = useState(null)
    const [loading, setLoading] = useState(true)
    const [expandedDetails, setExpandedDetails] = useState(false)
    const [isApproving, setIsApproving] = useState(false)
    const [isRejecting, setIsRejecting] = useState(false)
    const [isSearchingMarkets, setIsSearchingMarkets] = useState(false)
    const [selectedMatch, setSelectedMatch] = useState(null)

    // AUDIO FEEDBACK HANDLER (Task 13)
    const [isMuted, setIsMuted] = useState(() => {
        return localStorage.getItem('antigravity_mute_preference') === 'true';
    });
    const hasPlayedRef = useRef(false);

    // Toggle Mute
    const toggleMute = () => {
        const newState = !isMuted;
        setIsMuted(newState);
        localStorage.setItem('antigravity_mute_preference', newState);
    };

    // Play Success Chime using Web Audio API (No assets needed)
    const playSuccessSound = () => {
        if (isMuted) return;

        try {
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            if (!AudioContext) return;

            const ctx = new AudioContext();
            const now = ctx.currentTime;

            // Create oscillators for a pleasant "major third" chime (C5 + E5)
            const osc1 = ctx.createOscillator();
            const osc2 = ctx.createOscillator();
            const gain = ctx.createGain();

            osc1.type = 'sine';
            osc2.type = 'sine';

            osc1.frequency.setValueAtTime(523.25, now); // C5
            osc2.frequency.setValueAtTime(659.25, now); // E5

            // Envelope: Attack -> Decay
            gain.gain.setValueAtTime(0, now);
            gain.gain.linearRampToValueAtTime(0.3, now + 0.05); // Attack
            gain.gain.exponentialRampToValueAtTime(0.01, now + 1.5); // Decay

            osc1.connect(gain);
            osc2.connect(gain);
            gain.connect(ctx.destination);

            osc1.start(now);
            osc2.start(now);
            osc1.stop(now + 1.5);
            osc2.stop(now + 1.5);

            // Cleanup
            setTimeout(() => ctx.close(), 1600);
        } catch (e) {
            console.warn('Audio playback failed:', e);
        }
    };

    // Trigger Sound on Result Load
    useEffect(() => {
        if (result && !loading && !hasPlayedRef.current) {
            // Only play if we have a valid result (not error) and it's finished processing
            if (result.status !== 'failed' && result.status !== 'error' && result.status !== 'processing' && result.status !== 'pending') {
                playSuccessSound();
                hasPlayedRef.current = true;
            }
        }
    }, [result, loading]);

    useEffect(() => {
        if (!id) return

        console.log('Listening for identification result:', id)
        const unsub = onSnapshot(doc(db, 'identifications', id), (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data()
                setResult({ id: docSnap.id, ...data })

                // Initialize selection with the top match if not already set
                if (data.hallmarkMatches && data.hallmarkMatches.length > 0) {
                    setSelectedMatch(data.hallmarkMatches[0])
                }
            } else {
                setResult(null)
            }
            setLoading(false)
        }, (err) => {
            console.error('Error fetching result:', err)
            setLoading(false)
        })

        return () => unsub()
    }, [id])

    if (loading || (result && (result.status === 'pending' || result.status === 'processing'))) {
        return (
            <div className="results-page loading">
                <div className="loading-content">
                    <div className="loading-spinner large" />
                    <h2>{result?.status === 'processing' ? 'Processing...' : 'Initializing...'}</h2>
                    <p>
                        {result?.status === 'processing'
                            ? 'Our AI is analyzing your images and searching hallmark catalogues.'
                            : 'Analyzing your item...'}
                    </p>
                </div>
            </div>
        )
    }

    // Handle approval action
    const handleApprove = async () => {
        // CORRECTION: Ensure a match is selected before approving
        let finalSelection = selectedMatch;

        // If no match selected but we have candidates, default to the top one
        if (!finalSelection && result.hallmarkMatches && result.hallmarkMatches.length > 0) {
            finalSelection = result.hallmarkMatches[0];
            setSelectedMatch(finalSelection);
        }

        // Determine effective manufacturer name
        const manufacturerName = finalSelection?.manufacturer || result.manufacturer;

        // Block approval if still Ambiguous/Unknown and user hasn't selected a specific match
        if ((!manufacturerName || manufacturerName === 'Ambiguous' || manufacturerName === 'Unknown') && !finalSelection) {
            alert("Please select the correct manufacturer from the Side-by-Side Verification section before approving.");
            document.querySelector('.verification-section')?.scrollIntoView({ behavior: 'smooth' });
            return;
        }

        // Confirm override if applicable
        if (finalSelection && result.hallmarkMatches && result.hallmarkMatches.length > 0) {
            const topMatch = result.hallmarkMatches[0];
            // Only warn if they selected something OTHER than the top match, 
            // AND the top match wasn't just "Ambiguous" (which we ignored)
            if (finalSelection.id !== topMatch.id && topMatch.manufacturer !== 'Ambiguous') {
                const confirmed = window.confirm(
                    `You selected "${finalSelection.manufacturer}" instead of the top suggestion "${topMatch.manufacturer}".\n\n` +
                    `Proceed with "${finalSelection.manufacturer}"?`
                );
                if (!confirmed) return;
            }
        }

        setIsApproving(true)
        try {
            await approveIdentification(id, finalSelection)
        } catch (error) {
            console.error('Failed to approve:', error)
            alert('Failed to approve identification. Please try again.')
        } finally {
            setIsApproving(false)
        }
    }

    // Handle rejection action
    const handleReject = async () => {
        if (!window.confirm('Are you sure? This identification will be discarded and not saved to your history.')) {
            return
        }
        setIsRejecting(true)
        try {
            await rejectIdentification(id)
            navigate('/identify')
        } catch (error) {
            console.error('Failed to reject:', error)
            alert('Failed to reject identification. Please try again.')
        } finally {
            setIsRejecting(false)
        }
    }

    // Handle Manual Marketplace Search (Task 27)
    const handleMarketplaceSearch = async () => {
        // Use selected match, identified manufacturer, original inference, or fall back to empty string
        // The backend handle 'Unknown' by searching for item type only
        const targetManufacturer = selectedMatch?.manufacturer || result.identifiedManufacturer || result.manufacturer || '';

        setIsSearchingMarkets(true);
        try {
            const triggerSearch = httpsCallable(functions, 'triggerMarketplaceSearch');
            // Ensure we pass at least the ID. Manufacturer can be empty/unknown.
            const response = await triggerSearch({
                identificationId: id,
                manufacturer: targetManufacturer
            });

            if (response.data.success) {
                const count = response.data.count || 0;
                if (count === 0) {
                    alert(`Search complete. No new listings found for "${targetManufacturer}".`);
                } else {
                    alert(`Search complete. Found ${count} listings. The results below have been updated.`);
                }
            }
        } catch (error) {
            console.error('Marketplace search failed:', error);
            // Show the actual error message to the user for better debugging
            alert(`Search Failed: ${error.message || 'Unknown error'}`);
        } finally {
            setIsSearchingMarkets(false);
        }
    }

    if (result && result.status === 'failed') {
        return (
            <div className="results-page error">
                <div className="error-content">
                    <h2>Identification Failed</h2>
                    <p>Sorry, we encountered an error identified: {result.error || 'Unknown error'}</p>
                    <Link to="/identify" className="btn btn-primary">Try Again</Link>
                </div>
            </div>
        )
    }

    if (!result) {
        return (
            <div className="results-page error">
                <div className="error-content">
                    <h2>Result Not Found</h2>
                    <p>The identification result you're looking for doesn't exist.</p>
                    <Link to="/identify" className="btn btn-primary">Start New Identification</Link>
                </div>
            </div>
        )
    }

    // IMAGE PROXY HELPER (Task 8): Automatically wrap insecure HTTP links for hallmark images
    const getProxiedUrl = (url) => {
        if (!url) return url;
        if (url.startsWith('http://')) {
            const project = 'porcelain-identification-78c76';
            const region = 'europe-west1';
            return `https://${region}-${project}.cloudfunctions.net/proxyImage?url=${encodeURIComponent(url)}`;
        }
        return url;
    }

    // Calculate confidence from analysis results
    const calculateConfidence = () => {
        // PRIORITY 1: Primary AI visual confidence (SigLIP/dHash)
        if (result.visualConfidence !== undefined && result.visualConfidence !== null) {
            return result.visualConfidence
        }

        // FALLBACK: OCR/Logo scores
        if (result.analysis?.logos?.length > 0) {
            return Math.round(result.analysis.logos[0].score * 100)
        }
        if (result.analysis?.web?.webEntities?.length > 0) {
            return Math.round(result.analysis.web.webEntities[0].score * 100)
        }
        return 0
    }

    const confidence = calculateConfidence()
    const confidenceClass = confidence >= 80 ? 'high' : confidence >= 60 ? 'medium' : 'low'

    return (
        <div className="results-page">
            <div className="container">
                {/* Header */}
                <div className="results-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <Link to="/history" className="back-link">← Back to History</Link>
                        <h1>Identification Result</h1>
                    </div>
                    {/* Task 13: Mute Toggle */}
                    <button
                        onClick={toggleMute}
                        className="btn-icon"
                        title={isMuted ? "Unmute Sound" : "Mute Sound"}
                        style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}
                    >
                        {isMuted ? '🔇' : '🔊'}
                    </button>
                </div>

                {/* NEW: No Match Warning Banner (Task 41d) */}
                {result.match_status === 'no_match' && (
                    <div className="no-match-banner" style={{
                        background: 'rgba(255, 69, 58, 0.1)',
                        border: '1px solid rgba(255, 69, 58, 0.4)',
                        borderLeft: '4px solid #ff453a',
                        padding: '1.2rem',
                        borderRadius: '8px',
                        marginBottom: '1.5rem',
                        color: '#ffdddd'
                    }}>
                        <h3 style={{ margin: '0 0 0.5rem 0', color: '#ff453a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '1.2rem' }}>⚠️</span> No Confident Match Found
                        </h3>
                        <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: '1.5' }}>
                            The hallmark in your image did not score high enough to definitively match any manufacturer in our database. 
                            The closest visual candidates are shown below for reference only.
                        </p>
                    </div>
                )}

                {/* Main Result Card */}
                <div className="result-card card-glass">
                    <div className="result-main">
                        {/* Images - Dual Display if available */}
                        <div className="result-images-container" style={{ display: 'flex', gap: '10px' }}>
                            {/* Image 1: Main Item */}
                            <div className="result-image" style={{ flex: 1 }}>
                                <img
                                    src={result.images[0]?.url || '/placeholder.jpg'}
                                    alt="Identified item"
                                    onError={(e) => e.target.src = 'https://via.placeholder.com/400x400?text=Item'}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            </div>

                            {/* Image 2: Hallmark Crop */}
                            {result.images[1] && (
                                <div className="result-image" style={{ flex: 1 }}>
                                    <img
                                        src={result.images[1].url}
                                        alt="item hallmark"
                                        onError={(e) => e.target.style.display = 'none'}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Details */}
                        <div className="result-details">
                            <div className="detail-row">
                                <span className="detail-label">Possible Manufacturer(s):</span>
                                <span className="detail-value highlight">
                                    {selectedMatch?.manufacturer || result.manufacturer || 'Unknown'}
                                </span>
                            </div>



                            <div className="detail-row">
                                <span className="detail-label">Identified Symbols:</span>
                                <span className="detail-value">
                                    {result.inferred?.rawText || 'None detected'}
                                </span>
                            </div>

                            {/* Confidence Indicator */}
                            <div className="confidence-section" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '1rem' }}>
                                <div className={`confidence-pill ${confidenceClass}`}>
                                    {confidence}% Confidence
                                </div>
                                <p className="confidence-hint" style={{ margin: 0, fontSize: '0.85rem', color: '#888' }}>
                                    Based on {result.analysis?.labels?.length || 0} visual features.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Supporting Analysis */}
                    <div className="supporting-details">
                        <button
                            className="details-toggle"
                            onClick={() => setExpandedDetails(!expandedDetails)}
                        >
                            AI Analysis Breakdown
                            <span className={`toggle-icon ${expandedDetails ? 'open' : ''}`}>▼</span>
                        </button>

                        {expandedDetails && (
                            <div className="details-content">
                                <div className="detail-item">
                                    <strong>Detected Labels:</strong> {result.analysis?.labels?.join(', ') || 'N/A'}
                                </div>
                                <div className="detail-item">
                                    <strong>Logo detection:</strong> {result.analysis?.logos?.map(l => l.description).join(', ') || 'None recognized'}
                                </div>
                                <div className="detail-item">
                                    <strong>Raw Text:</strong> {result.analysis?.text?.join(' ') || 'None recognized'}
                                </div>
                                {result.geminiReasoning && (
                                    <div className="gemini-reasoning" style={{
                                        marginTop: '1rem',
                                        padding: '1rem',
                                        borderLeft: '4px solid var(--gold-primary)',
                                        background: 'rgba(212, 175, 55, 0.05)',
                                        fontSize: '0.95rem',
                                        fontStyle: 'italic',
                                        color: '#ddd'
                                    }}>
                                        <strong>AI Verification Note:</strong> "{result.geminiReasoning}"
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* NEW: Side-by-Side Verification Area */}
                <div className="verification-section card-glass" style={{ marginBottom: '2rem', padding: '1.5rem' }}>
                    <div className="section-header" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1rem' }}>
                        <span style={{ fontSize: '1.5rem' }}>⚖️</span>
                        <h2 style={{ margin: 0 }}>Side-by-Side Verification</h2>
                    </div>
                    <div className="verification-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                        gap: '1.5rem'
                    }}>
                        <div className="verification-column">
                            <h3 style={{ fontSize: '0.9rem', color: '#888', textTransform: 'uppercase', marginBottom: '10px' }}>Your Hallmark</h3>
                            <div className="img-box" style={{ background: '#111', borderRadius: '8px', overflow: 'hidden', aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.1)' }}>
                                <img
                                    src={result.images?.[1]?.url || result.images?.[0]?.url}
                                    alt="User hallmark"
                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                />
                            </div>
                        </div>
                        <div className="verification-column">
                            <h3 style={{ fontSize: '0.9rem', color: '#888', textTransform: 'uppercase', marginBottom: '10px' }}>Identified Match</h3>
                            <div className="img-box" style={{ background: '#111', borderRadius: '8px', overflow: 'hidden', aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.1)' }}>
                                {selectedMatch?.imageUrl || result.matchedHallmarkImage ? (
                                    <img
                                        src={getProxiedUrl(selectedMatch?.imageUrl || result.matchedHallmarkImage)}
                                        alt="Database match"
                                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                    />
                                ) : (
                                    <div style={{ color: '#666', fontStyle: 'italic' }}>No reference image available</div>
                                )}
                            </div>
                            <div style={{ marginTop: '10px', textAlign: 'center' }}>
                                <strong>{selectedMatch?.manufacturer || result.manufacturer}</strong>
                                {selectedMatch && <div style={{ fontSize: '0.8rem', color: '#aaa' }}>{selectedMatch.period}</div>}
                            </div>
                        </div>
                    </div>

                    {/* Candidate Selection List (if ambiguous or multiple matches) */}
                    {result.hallmarkMatches && result.hallmarkMatches.length > 1 && (
                        <div className="candidates-list" style={{ marginTop: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '1rem' }}>
                            <h4 style={{ marginBottom: '0.5rem', color: '#ddd' }}>Did we miss? Choose another candidate:</h4>
                            <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '10px' }}>
                                {result.hallmarkMatches.map(match => (
                                    <div
                                        key={match.id}
                                        onClick={() => setSelectedMatch(match)}
                                        style={{
                                            minWidth: '120px',
                                            padding: '10px',
                                            borderRadius: '8px',
                                            background: selectedMatch?.id === match.id ? 'rgba(212, 175, 55, 0.2)' : 'rgba(255,255,255,0.05)',
                                            border: selectedMatch?.id === match.id ? '1px solid var(--gold-primary)' : '1px solid transparent',
                                            cursor: 'pointer',
                                            textAlign: 'center'
                                        }}
                                    >
                                        <div style={{ width: '80px', height: '80px', margin: '0 auto 8px', borderRadius: '4px', overflow: 'hidden', background: '#000' }}>
                                            <img src={getProxiedUrl(match.imageUrl)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        </div>
                                        <div style={{ fontSize: '0.8rem', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{match.manufacturer}</div>
                                        <div style={{ fontSize: '0.7rem', color: '#aaa' }}>{match.matchScore}% Match</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Approval Section for pending_approval status */}
                {result.status === 'pending_approval' && (
                    <div className="approval-section card-glass" style={{ marginBottom: '2rem' }}>
                        <h2>✋ Verify Identification</h2>
                        <p className="approval-instruction">
                            Please compare the hallmark on your item with the matched catalogue marks below.
                            If the identification is correct, approve it to save to your history.
                        </p>
                        <div className="approval-actions">
                            <button
                                className="btn btn-success btn-large"
                                onClick={handleApprove}
                                disabled={isApproving || isRejecting}
                            >
                                {isApproving ? 'Saving...' : '✓ Correct - Save to History'}
                            </button>
                            <button
                                className="btn btn-danger btn-large"
                                onClick={handleReject}
                                disabled={isApproving || isRejecting}
                            >
                                {isRejecting ? 'Discarding...' : '✗ Incorrect - Discard'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Approved status banner */}
                {result.status === 'approved' && (
                    <div className="status-banner approved" style={{ marginBottom: '2rem' }}>
                        ✓ This identification has been verified and saved to your history.
                    </div>
                )}

                {/* Hallmark Matches Section */}
                {result.hallmarkMatches && result.hallmarkMatches.length > 0 && (
                    <div className="hallmark-section card-glass" style={{ marginBottom: '2rem' }}>
                        <h2>🔍 Matched Hallmarks</h2>
                        <p className="section-description">
                            Compare these hallmarks with the mark on your item to verify identification.
                        </p>
                        <div className="hallmark-grid">
                            {result.hallmarkMatches.sort((a, b) => b.matchScore - a.matchScore).slice(0, 3).map((match, idx) => (
                                <div
                                    key={idx}
                                    className={`hallmark-card ${match.isVerified ? 'verified' : ''} ${selectedMatch?.id === match.id ? 'selected' : ''}`}
                                    onClick={() => setSelectedMatch(match)}
                                    style={{
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        border: selectedMatch?.id === match.id ? '2px solid var(--gold-primary)' : '1px solid rgba(255,255,255,0.1)',
                                        background: selectedMatch?.id === match.id ? 'rgba(212, 175, 55, 0.1)' : 'transparent',
                                        position: 'relative'
                                    }}
                                >
                                    <div className="hallmark-header">
                                        <strong className="manufacturer-name">{match.manufacturer}</strong>
                                        <div className="match-badges">
                                            {match.isVerified && <span className="verified-badge">✓ Verified</span>}
                                            <span className="match-score">{Math.round(match.matchScore)}% match</span>
                                        </div>
                                    </div>

                                    <div className="hallmark-images-row">
                                        <div className="hallmark-image-wrapper">
                                            {match.markImageUrl ? (
                                                <img src={getProxiedUrl(match.markImageUrl)} alt={`${match.manufacturer} mark`} />
                                            ) : (
                                                <div className="image-placeholder">No Mark Image</div>
                                            )}
                                            <span className="image-label">Hallmark</span>
                                        </div>
                                        <div className="hallmark-image-wrapper">
                                            <img
                                                src={result.images?.[1]?.url || result.images?.[0]?.url}
                                                alt="Your item's mark"
                                                onError={(e) => e.target.src = '/placeholder.jpg'}
                                            />
                                            <span className="image-label">Item</span>
                                        </div>
                                    </div>

                                    <div className="hallmark-details">
                                        <p className="description">{match.description}</p>
                                        <p className="period">Period: {match.period || 'Unknown'}</p>
                                    </div>

                                    {selectedMatch?.id === match.id && (
                                        <div className="selection-badge" style={{
                                            position: 'absolute',
                                            top: '-10px',
                                            right: '-10px',
                                            background: 'var(--gold-primary)',
                                            color: '#000',
                                            borderRadius: '50%',
                                            width: '24px',
                                            height: '24px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '0.8rem',
                                            fontWeight: 'bold',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                            zIndex: 2
                                        }}>
                                            ✓
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Marketplace Comparisons */}
                <div className="similar-section card-glass" style={{ marginBottom: '2rem', padding: '1.5rem' }}>
                    <div className="section-header" style={{ marginBottom: '1rem' }}>
                        <h2 style={{ margin: 0 }}>Live Marketplace Comparisons</h2>
                    </div>

                    <div className="search-actions" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                        <button
                            className="btn btn-secondary btn-sm"
                            onClick={handleMarketplaceSearch}
                            disabled={isSearchingMarkets || loading}
                            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                        >
                            {isSearchingMarkets ? (
                                <>
                                    <span className="spinner-small"></span> Searching...
                                </>
                            ) : (
                                <>
                                    <span>🔄</span> Search Markets for {selectedMatch?.manufacturer || result.identifiedManufacturer || result.manufacturer || 'Current'}
                                </>
                            )}
                        </button>
                        <a
                            href={`https://www.google.com/searchbyimage?image_url=${encodeURIComponent(result.images?.[0]?.url)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-secondary"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                        >
                            <span>📸</span>
                            {result.images?.[1] ? 'Search Item on Lens' : 'Search Image on Lens'}
                        </a>

                        {result.images?.[1] && (
                            <a
                                href={`https://www.google.com/searchbyimage?image_url=${encodeURIComponent(result.images?.[1]?.url)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-secondary"
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                            >
                                <span>🔍</span> Search Hallmark on Lens
                            </a>
                        )}
                    </div>

                    <div className="similar-grid">
                        {result.matches && result.matches.length > 0 ? (
                            result.matches.slice(0, 8).map((match, idx) => (
                                <div key={idx} className="similar-card">
                                    <div className="similar-image">
                                        <img src={match.image} alt={match.title} onError={(e) => e.target.src = 'https://via.placeholder.com/150x150?text=Item'} />
                                    </div>
                                    <div className="similar-info">
                                        <h4>{match.title}</h4>
                                        <p>{match.source} • {match.price}</p>
                                        <a href={match.url} target="_blank" rel="noopener noreferrer" className="view-link">View Listing</a>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="no-matches-container" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem' }}>
                                <p className="no-matches" style={{ marginBottom: '1rem' }}>No direct text matches found.</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Disclaimer */}
                <div className="disclaimer" style={{ opacity: 0.6, fontSize: '0.8rem', textAlign: 'center' }}>
                    <p>
                        <strong>Disclaimer:</strong> Identification and valuation are estimates based on AI analysis.
                        Results are not guaranteed.
                    </p>
                </div>

                {/* Actions */}
                <div className="result-actions" style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '2rem' }}>
                    <Link to="/identify" className="btn btn-primary">Identify Another</Link>
                    <Link to="/history" className="btn btn-secondary">View History</Link>
                </div>
            </div>
        </div>
    )
}

export default ResultsPage
